import React from "react";
import { ArrowRight, ChevronRight } from "lucide-react";

import { TypewriterEffect } from "../ui/typewriter-effect";
import Bucket from "../ui/bucket";
import { ShopifyMark } from "./icons";

const PIPELINE = ["Intake", "Erasure", "Assessment", "Reimage", "Grade", "Resell"];

const HEADLINE_WORDS = [
  { text: "from" },
  { text: "Erasure" },
  { text: "&" },
  { text: "Assessment" },
  { text: "to" },
  { text: "Resale.", className: "text-brand-magenta" },
];

const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-brand-bg font-body"
    >
      {/* atmosphere */}
      <div className="bg-dot-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <div
        className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-brand-orchid/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 top-40 h-[26rem] w-[26rem] rounded-full bg-brand-magenta/15 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-8 lg:py-28">
        {/* ── Left: copy ── */}
        <div>
          <span
            className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-line bg-white/80 px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum shadow-sm backdrop-blur"
            style={{ animationDelay: "0ms" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-magenta" />
            IT Asset Recovery Platform
          </span>

          {/* full headline for screen readers / SEO; animated version below is decorative */}
          <h1 className="sr-only">
            IT Asset Recovery, from Erasure &amp; Assessment to Resale.
          </h1>
          <div
            aria-hidden="true"
            className="animate-fade-up mt-6"
            style={{ animationDelay: "80ms" }}
          >
            <div className="font-display text-4xl font-semibold leading-[1.06] tracking-tight text-brand-plum sm:text-5xl lg:text-6xl">
              IT Asset Recovery,
            </div>
            <TypewriterEffect
              words={HEADLINE_WORDS}
              className="mt-1 text-left font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-plum sm:text-5xl lg:text-6xl"
            />
          </div>

          <p
            className="animate-fade-up mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            Furloop helps ITADs, refurbishers, buyback operators, and device recovery teams turn used IT
            assets into verified, resale-ready inventory — through one connected workflow for erasure,
            assessment, reimaging, grading, reporting, and resale.
          </p>

          {/* Works with Shopify */}
          <div
            className="animate-fade-up mt-6 inline-flex items-center gap-2.5 rounded-full border border-line bg-white px-3.5 py-2 shadow-sm"
            style={{ animationDelay: "220ms" }}
          >
            <ShopifyMark className="h-5 w-5" />
            <span className="text-sm font-medium text-brand-plum">
              Works with Shopify
            </span>
            <span className="hidden text-sm text-muted sm:inline">— supports Shopify store owners</span>
          </div>

          {/* CTAs */}
          <div
            className="animate-fade-up mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
            style={{ animationDelay: "280ms" }}
          >
            <a
              href="#register"
              className="group inline-flex items-center gap-2 rounded-full bg-cta-gradient px-6 py-3 text-sm font-semibold text-white no-underline shadow-lg shadow-[#304BEF]/25 transition-transform duration-200 hover:-translate-y-0.5 hover:no-underline"
            >
              Register Interest
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
            <a
              href="#automation"
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-brand-plum no-underline transition-colors hover:border-brand-orchid hover:text-brand-magenta hover:no-underline"
            >
              Explore Robot Automation R&D
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>

          {/* Pipeline */}
          <div
            className="animate-fade-up mt-10"
            style={{ animationDelay: "340ms" }}
          >
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
              {PIPELINE.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="rounded-full border border-line bg-white/70 px-3 py-1 text-xs font-medium text-brand-plum">
                    {step}
                  </span>
                  {i < PIPELINE.length - 1 && (
                    <ChevronRight className="h-3.5 w-3.5 text-brand-orchid" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="mt-3 max-w-lg text-xs leading-relaxed text-muted">
              Built for teams processing laptops, desktops, mobile devices, and recovered IT assets across
              resale, redeployment, repair, and recycling workflows.
            </p>
          </div>
        </div>

        {/* ── Right: funnel showpiece ── */}
        <div className="animate-fade-up relative" style={{ animationDelay: "200ms" }}>
          <div className="relative rounded-3xl border border-line bg-white/50 p-6 pt-14 shadow-xl shadow-brand-plum/5 backdrop-blur-sm sm:p-10 sm:pt-16">
            <Bucket />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
